import { inject, Injectable } from '@angular/core';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap, toArray } from 'rxjs/operators';
import { InsuranceType, InsuranceTypeDto, dtoToInsuranceType } from '@models/insurance-type';
import { HttpService, Params } from '../http/http.service';
import { HttpParams } from '@angular/common/http';
import { User, UserDto, dtoToUser, userToDto } from '@models/user';
import { Customer, CustomerDto, customerToDto, dtoToCustomer } from '@models/customer';
import { City, CityDto, cityFromDto } from '@models/city';
import { Title, TitleDto } from '@models/title';
import { OrderState, orderStateToContractRequestDto } from 'app/order/models/order-state.model';
import { ParamEncoder } from './param-encoder.model';
import { Contract, ContractDto, contractFromDto } from '@models/contract';
import { PoolObject } from '../contract/poolObject.model';
import { Insurant, InsurantDto, insurantFromDto, insurantToDto } from '@models/insurant';
import { InsurableObject } from '../contract/insurableObject.model';
import { PaymentMethod, PaymentMethodDto, paymentMethodFromDto, paymentMethodToDto } from '@models/payment-method';
import {
    CreateDamageReportDto,
    DamageReportDto,
    DamageReportFileAttachment,
    DamageReportFileAttachmentDto,
    damageReportFileAttachmentFromDto,
    DamageDocumentType,
    DocumentTypeDto,
    documentTypeFromDto,
} from '@models/damage-report';
import { Product } from '@models/product';
import { getPriceRequestDto } from 'app/order/models/price-request';
import { NotificationService } from '../components/notification/notification.service';
import { NotificationType } from '../components/notification/notification-type.enum';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class InsurenetInterfaceService {
    private gkvNames: string[] = [];
    private http: HttpService = inject(HttpService);
    private path = environment.apiPath;
    private notificationsService = inject(NotificationService);

    // #region auth

    /**
     * @description Attempt to login the user with InsureNet. If multiple path sets are defined
     * (i.e. there are multiple instances of InsureNet, for instance for the ZZV while the
     * sale is finalized) it will attempt to login the user at each one, and the first one that
     * returns a positive result will be set as the current path set for all subsequent requests
     * from the context of the service portal.
     * @param {string} email the user's emailm adress - part of the credentials for login
     * @param {string} password the user's login password
     * @returns {Observable<User>} an observable of the user that just logged in when the attempt
     * was successful.
     */
    public login(email: string, password: string): Observable<User> {
        return this.http.post<UserDto>(`${this.path.base}Login`, { email, password }, { notify: 'NONE' }).pipe(
            map((dto: UserDto) => dtoToUser(dto)),
            catchError(() => {
                this.notificationsService.notify(
                    'Unbekannter Benutzer oder falsches Kennwort.',
                    NotificationType.WARNING,
                    7000,
                );
                return EMPTY;
            }),
        );
    }

    public requestPasswordReset(email: string, insuranceId: string): Observable<string> {
        const params = new HttpParams({ encoder: new ParamEncoder() })
            .set('email', email)
            .set('InsuranceNo', insuranceId);

        return this.http.put<string>(`${this.path.base}ForgetPassword`, {}, { params, notify: 'NONE' }).pipe(
            catchError(() => {
                this.notificationsService.notify(
                    'Benutzer nicht gefunden. Bitte überprüfe die eingegebene E-Mail-Adresse und Versicherungsnummer.',
                    NotificationType.WARNING,
                    7000,
                );
                return EMPTY;
            }),
        );
    }

    public confirmPasswordReset(token: string): Observable<void> {
        const params = new HttpParams().set('token', token);

        return this.http.post<void>(`${this.path.base}ConfirmForgetPassword`, {}, { params }).pipe(
            catchError(() => {
                this.notificationsService.notify(
                    'Dieser Link ist abgelaufen oder ungültig',
                    NotificationType.WARNING,
                    7000,
                );
                return EMPTY;
            }),
        );
    }

    public updateLogin(email: string, password?: string): Observable<string> {
        return this.http.put<string>(`${this.path.base}UpdateLogin`, { email, password });
    }

    // #endregion
    // #region customer

    public updateCustomer(customer: Customer): Observable<Customer> {
        const customerDto = customerToDto(customer);
        return this.http
            .put<CustomerDto>(`${this.path.base}CustomerPersonalData`, customerDto)
            .pipe(map((dto) => dtoToCustomer(dto)));
    }

    public updateBankData(customer: Customer): Observable<Customer> {
        const customerDto = customerToDto(customer);
        return this.http
            .put<CustomerDto>(`${this.path.base}CustomerBankDetails`, customerDto)
            .pipe(map((dto) => dtoToCustomer(dto)));
    }

    public deactivateAccount(password: string, user: User): Observable<void> {
        const userDto = userToDto(user);
        return this.http.put(`${this.path.base}DeactivateUser?pw=${password}`, { ...userDto }, { notify: 'NONE' }).pipe(
            catchError((err) => {
                // Test if the error is because the wrong password was entered
                if ('ErrorCode' in err && err.ErrorCode === 22) {
                    return throwError(() => err);
                }
                this.notificationsService.notify(
                    'Es ist ein Fehler aufgetreten, bitte versuche es erneut',
                    NotificationType.CRITICAL,
                    7000,
                );
                return EMPTY;
            }),
            map(() => null),
        );
    }

    // #endregion

    public getInsuranceTypes(): Observable<InsuranceType[]> {
        return this.http
            .get<InsuranceTypeDto[]>(`${this.path.base}InsuranceTypes`, { notify: 'ERROR' })
            .pipe(map((dtos: InsuranceTypeDto[]) => dtos.flat().map((dto) => dtoToInsuranceType(dto))));
    }

    public getCity(zip: string, paramsOverride: Params = {}): Observable<City> {
        const params = new HttpParams().set('zip', zip);
        return this.http.get<CityDto[]>(`${this.path.base}Cities`, { params, notify: 'NONE', ...paramsOverride }).pipe(
            map((cities) => cityFromDto(cities[0])),
            catchError(() => EMPTY),
        );
    }

    public getTitles(paramsOverride: Params = {}): Observable<Title[]> {
        return this.http.get<TitleDto[]>(`${this.path.base}Titles`, { notify: 'ERROR', ...paramsOverride }).pipe(
            switchMap((titles) =>
                from(titles).pipe(
                    map((title) => ({ id: title.Id, description: title.Description }) as Title),
                    toArray(),
                ),
            ),
        );
    }

    public getInsurableObjects(insuranceTypeId: number | string): Observable<{ label: string; value: number }[]> {
        return this.http
            .get<InsurableObject[]>(`${this.path.base}InsuranceObjects/${insuranceTypeId}`, { notify: 'ERROR' })
            .pipe(
                map((insurableObjectDtos: InsurableObject[]) =>
                    insurableObjectDtos.map((dto: InsurableObject) => ({ label: dto.Description, value: dto.Id })),
                ),
            );
    }

    public getDamageDocumentTypes(insuranceTypeId: number): Observable<DamageDocumentType[]> {
        const params = new HttpParams().set('InsuranceTypeId', insuranceTypeId);
        return this.http
            .get<
                DocumentTypeDto[]
            >(`${this.path.base}DocumentTypes`, { params, retryCount: 2, notify: 'NONE', spin: false })
            .pipe(map((values) => values.map(documentTypeFromDto)));
    }

    public getDiscount(discountCode: string, insuranceTypeId: number | string): Observable<number> {
        const params = new HttpParams().set('DiscountCode', discountCode);
        return this.http.get<number>(`${this.path.base}InsuranceTypes/${insuranceTypeId}/DiscountFactor`, {
            params,
            notify: 'NONE',
        });
    }

    public getPrice(state: OrderState, product: Product, insuranceType: InsuranceType): Observable<number> {
        const request = getPriceRequestDto(state, product, insuranceType);
        return this.http.post<number>(`${this.path.base}InsurancePrice`, request, {
            notify: 'NONE',
            spin: false,
        });
    }

    public getGKVList(): Observable<string[]> {
        if (this.gkvNames.length > 0) {
            return of(this.gkvNames);
        } else {
            return this.http
                .get<string[]>(`${this.path.base}StatutoryHealthInsurance`, {
                    retryCount: 2,
                    notify: 'NONE',
                    spin: false,
                })
                .pipe(
                    tap((names: string[]) => (this.gkvNames = names)),
                    shareReplay({ refCount: true, bufferSize: 1 }),
                );
        }
    }

    public updateObjectPool(contractId: number, poolObjects: PoolObject[]): Observable<PoolObject[]> {
        return this.http.put(`${this.path.base}Contracts/${contractId}/ObjectPool`, poolObjects);
    }

    public updateInsurants(contractId: number, insurants: Insurant[]): Observable<Insurant[]> {
        return this.http
            .put<InsurantDto[]>(
                `${this.path.base}Contracts/${contractId}/Insurants`,
                insurants.map((insurant: Insurant) => insurantToDto(insurant)),
            )
            .pipe(map((insurantDtos: InsurantDto[]) => insurantDtos.map((dto: InsurantDto) => insurantFromDto(dto))));
    }

    public getConsultationProtocol(
        state: OrderState,
        product: Product,
        insuranceType: InsuranceType,
        customer?: Customer,
    ): Observable<Blob> {
        return this.http.post<Blob>(
            `${this.path.base}ConsultationProtocol`,
            orderStateToContractRequestDto(state, product, insuranceType, customer),
            { responseType: 'blob', notify: 'ERROR' },
        );
    }

    public submitContractRequest(
        state: OrderState,
        product: Product,
        insuranceType: InsuranceType,
        customer?: Customer,
    ): Observable<unknown> {
        return this.http.post<unknown>(
            `${this.path.base}ContractRequests`,

            orderStateToContractRequestDto(state, product, insuranceType, customer),
        );
    }

    public getContracts(): Observable<Contract[]> {
        return this.http
            .get<ContractDto[]>(`${this.path.base}Contracts`, { retryCount: 2, notify: 'ERROR' })
            .pipe(
                map((contracts: ContractDto[]) => contracts.map((contract: ContractDto) => contractFromDto(contract))),
            );
    }

    // #region payment methods
    public getPaymentMethods(contractId: number): Observable<PaymentMethod[]> {
        const url = `${this.path.payment}business-case/payment-method/${contractId}`;

        return this.http
            .get<PaymentMethodDto[]>(url, { notify: 'ERROR' })
            .pipe(map((dtos: PaymentMethodDto[]) => dtos.map((dto: PaymentMethodDto) => paymentMethodFromDto(dto))));
    }

    public createPaymentMethod(paymentMethod: Omit<PaymentMethod, 'id'>): Observable<PaymentMethod[]> {
        const { contractId } = paymentMethod;
        const url = `${this.path.payment}contracts/${contractId}/payment-methods`;
        const dto = paymentMethodToDto(paymentMethod);

        return this.http
            .post<PaymentMethodDto[]>(url, dto)
            .pipe(map((dtos: PaymentMethodDto[]) => dtos.map((item: PaymentMethodDto) => paymentMethodFromDto(item))));
    }

    public updatePaymentMethod(paymentMethod: PaymentMethod): Observable<PaymentMethod[]> {
        const { contractId } = paymentMethod;
        const url = `${this.path.payment}contracts/${contractId}/payment-methods`;
        const dto = paymentMethodToDto(paymentMethod);

        return this.http
            .put<PaymentMethodDto[]>(url, dto, { notify: 'ALL' })
            .pipe(map((dtos: PaymentMethodDto[]) => dtos.map((item: PaymentMethodDto) => paymentMethodFromDto(item))));
    }

    public deletePaymentMethod(paymentMethod: PaymentMethod): Observable<PaymentMethod[]> {
        const { id, contractId } = paymentMethod;
        const url = `${this.path.payment}contracts/${contractId}/payment-methods/${id}`;

        return this.http
            .delete<PaymentMethodDto[]>(url, { notify: 'ALL' })
            .pipe(map((dtos: PaymentMethodDto[]) => dtos.map((item: PaymentMethodDto) => paymentMethodFromDto(item))));
    }
    // #endregion

    // #region damages
    public reportDamage(damage: CreateDamageReportDto): Observable<DamageReportDto> {
        return this.http.post<DamageReportDto>(`${this.path.base}Damages`, damage);
    }

    public uploadDamageDocument(damageId: number, formData: FormData): Observable<DamageReportFileAttachmentDto> {
        return this.http
            .upload(`${this.path.base}Damages/${damageId}/DamageDocuments`, formData)
            .pipe(
                map((dto: { AddDamageDocumentResult: DamageReportFileAttachmentDto }) => dto.AddDamageDocumentResult),
            ) as Observable<DamageReportFileAttachmentDto>;
    }

    public getDamages(contractId: number): Observable<DamageReportDto[]> {
        return this.http.get<DamageReportDto[]>(`${this.path.base}Damages/${contractId}`, { notify: 'ERROR' }).pipe(
            // TODO: Check what is returned if there are no damages, maybe this can be simplified.
            map((val) => (!Array.isArray(val) ? [] : val)),
        );
    }

    public getDamageReportFileAttachments(damageId: number): Observable<DamageReportFileAttachment[]> {
        return this.http
            .get<
                DamageReportFileAttachmentDto[]
            >(`${this.path.base}Damages/${damageId}/DamageDocuments`, { notify: 'ERROR' })
            .pipe(map((dtos) => dtos.map((dto) => damageReportFileAttachmentFromDto(dto))));
    }

    public getDamageDocumentBlob(damageId: number, docId: number): Observable<Blob> {
        return this.http.get<Blob>(`${this.path.base}Damages/${damageId}/DamageDocuments/${docId}`, {
            responseType: 'blob',
            notify: 'ERROR',
        });
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public postDamageNote(damageId: number, note: string): Observable<any> {
        return this.http.post(`${this.path.base}Damages/${damageId}/AddNote`, { Note: note });
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public getDamageNotes(damageId: number): Observable<any> {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        return this.http.get<any>(`${this.path.base}Damages/${damageId}/Notes`, { notify: 'ERROR' });
    }
    // #endregion
}
